import { Date as ReviewDate } from './types';

// Multi-officer requests fan each shift into N near-identical date rows
// (officer_index 0..N-1). The review table and schedule modal must show ONE row
// per logical shift, while still tracking each officer slot (one per copy) so
// up to N officers can be assigned. This helper regroups the flat `dates` array
// back into per-shift groups.

export interface ShiftGroup {
    // Stable identity of the shift (mirrors the backend copy signature).
    key: string;
    // The primary (officer_index 0) row — drives the summary row.
    primaryId: number | undefined;
    date: ReviewDate['date'];
    startTime: ReviewDate['startTime'];
    endTime: ReviewDate['endTime'];
    shift: string | null;
    shiftType: ReviewDate['shiftType'];
    blockDate: ReviewDate['blockDate'];
    accessLocations?: string[] | undefined;
    visitorCount?: number | undefined;
    // How many officer rows currently exist for this shift.
    officerCount: number;
    // Every copy row, ordered by officer_index.
    copies: ReviewDate[];
    // Names of the officers already assigned (system or free-text).
    assignedOfficerNames: string[];
    // security_officer_id values already assigned — used to avoid double-booking.
    assignedOfficerIds: number[];
    // Ids of the copy rows that still have no officer.
    freeSlotIds: number[];
}

// Identity of a shift shared by all its officer rows (base + copies).
export const shiftSignature = (date: ReviewDate): string => [
    date.date,
    date.shift ?? '',
    date.shiftType ?? '',
    date.startTime,
    date.endTime,
    date.blockDate ?? '',
].join('|');

export const groupShiftsByShift = (dates: ReviewDate[] | null | undefined): ShiftGroup[] => {
    if (!dates || dates.length === 0) {
        return [];
    }

    const groups = new Map<string, ReviewDate[]>();
    dates.forEach((date) => {
        const key = shiftSignature(date);
        const bucket = groups.get(key) ?? [];
        bucket.push(date);
        groups.set(key, bucket);
    });

    return Array.from(groups.entries()).map(([key, rows]): ShiftGroup | null => {
        const copies = [...rows].sort((a, b) => (a.officerIndex ?? 0) - (b.officerIndex ?? 0));
        const primary = copies.find((c) => (c.officerIndex ?? 0) === 0) ?? copies[0];
        if (!primary) {
            return null;
        }

        const assignedOfficerNames = copies
            .filter((c) => c.securityOfficer)
            .map((c) => c.securityOfficer as string);
        const assignedOfficerIds = copies
            .filter((c) => c.securityOfficerId)
            .map((c) => c.securityOfficerId as number);
        const freeSlotIds = copies
            .filter((c) => !c.securityOfficer && c.id !== undefined)
            .map((c) => c.id as number);

        return {
            key,
            primaryId: primary?.id,
            date: primary.date,
            startTime: primary.startTime,
            endTime: primary.endTime,
            shift: primary.shift,
            shiftType: primary.shiftType,
            blockDate: primary.blockDate,
            accessLocations: primary.accessLocations,
            visitorCount: primary.visitorCount,
            officerCount: copies.length,
            copies,
            assignedOfficerNames,
            assignedOfficerIds,
            freeSlotIds,
        };
    }).filter((group): group is ShiftGroup => group !== null);
};

// Mirrors SiteShift::TYPE_* on the backend.
const SHIFT_TYPE_NIGHT = 10;
const SHIFT_TYPE_ALL_DAY = 20;
const SHIFT_TYPE_CUSTOM = 30;

const pluralise = (count: number, noun: string): string => `${count} ${noun}${count === 1 ? '' : 's'}`;

// Summarises the shift groups as "2 Days, 1 Night". Day/Open Day count as
// days, Night as nights, a 24/7 block as one of each, and Custom by its start
// hour (07:00-18:59 is a day).
export const summariseShiftCounts = (groups: ShiftGroup[]): string => {
    let days = 0;
    let nights = 0;
    const allDayBlocks = new Set<string>();

    groups.forEach((group) => {
        const type = Number(group.shiftType ?? 0);
        if (type === SHIFT_TYPE_ALL_DAY) {
            allDayBlocks.add(String(group.blockDate || group.date));
            return;
        }
        if (type === SHIFT_TYPE_NIGHT) {
            nights += 1;
            return;
        }
        if (type === SHIFT_TYPE_CUSTOM) {
            const startHour = parseInt(String(group.startTime).slice(0, 2), 10);
            if (startHour >= 7 && startHour < 19) {
                days += 1;
            } else {
                nights += 1;
            }
            return;
        }
        days += 1;
    });

    days += allDayBlocks.size;
    nights += allDayBlocks.size;

    const parts: string[] = [];
    if (days > 0) {
        parts.push(pluralise(days, 'Day'));
    }
    if (nights > 0) {
        parts.push(pluralise(nights, 'Night'));
    }
    return parts.length > 0 ? parts.join(', ') : '0 Days';
};
